import React, { useId, useRef, useState } from "react";
import {
  laserErrorMessage,
  VerifiedDrawing,
  verifyLaserDxfs,
  calculateSheetNesting,
} from "../../api/laserCosting";
import { MAX_NEST_PIECES, NestResult } from "./nestingLayout";

const colors = [
  "#2563eb",
  "#d97706",
  "#059669",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#be185d",
  "#4d7c0f",
];
type Row = VerifiedDrawing & { id: number; quantity: number | "" };

export default function SheetNesting() {
  const previewId = useId();
  const [length, setLength] = useState<number | "">(1250);
  const [width, setWidth] = useState<number | "">(2500);
  const [thickness, setThickness] = useState<number | "">("");
  const [spacing, setSpacing] = useState<number | "">("");
  const [edgeMargin, setEdgeMargin] = useState<number | "">(0);
  const [zoom, setZoom] = useState(1);
  const [units, setUnits] = useState("mm");
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState<"upload" | "nesting" | null>(null);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<NestResult | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const nextId = useRef(0);
  const invalidate = () => {
    setResult(null);
    setMessage("");
  };
  const upload = async (files: File[]) => {
    if (!files.length) return;
    invalidate();
    if (files.length + rows.length > 30) {
      setMessage("Import up to 30 DXFs per run.");
      return;
    }
    if (
      files.some(
        (f) =>
          !f.name.toLowerCase().endsWith(".dxf") ||
          !f.size ||
          f.size > 10 * 1024 * 1024
      )
    ) {
      setMessage("Choose non-empty DXF files, each no larger than 10 MB.");
      return;
    }
    const names = [
      ...rows.map((r) => r.filename.toLowerCase()),
      ...files.map((f) => f.name.toLowerCase()),
    ];
    if (new Set(names).size !== names.length) {
      setMessage(
        "Duplicate DXF names found. Remove the existing drawing or rename the file."
      );
      return;
    }
    setBusy("upload");
    try {
      const drawings = await verifyLaserDxfs(files, units, "nesting");
      setRows((prev) => [
        ...prev,
        ...drawings.map((d) => ({ ...d, id: nextId.current++, quantity: 1 })),
      ]);
    } catch (error) {
      setMessage(await laserErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const calculate = async (event: React.FormEvent) => {
    event.preventDefault();
    invalidate();
    if (rows.some((r) => r.status !== "verified")) {
      setMessage("Remove or re-import failed drawings before calculating.");
      return;
    }
    setBusy("nesting");
    try {
      setResult(
        await calculateSheetNesting({
          length: Number(length),
          width: Number(width),
          thickness: Number(thickness),
          ...(spacing === "" ? {} : { spacing: Number(spacing) }),
          edge_margin: Number(edgeMargin),
          items: rows.map((r) => ({
            verification: r.verification ?? "",
            quantity: Number(r.quantity),
          })),
        })
      );
      setSheetIndex(0);
      setZoom(1);
    } catch (error) {
      setMessage(await laserErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const sheet = result?.sheets[sheetIndex];
  const utilization = result
    ? result.sheets.reduce((total, s) => total + s.utilization, 0) /
      result.sheets.length
    : 0;
  const totalPieces = rows.reduce(
    (total, row) => total + Number(row.quantity),
    0
  );
  return (
    <section aria-label="Sheet requirements calculator">
      <h2>Laser cutting nesting</h2>
      <p>
        Enter the sheet size and thickness, import one piece per DXF, then enter
        the quantities required. All dimensions are in mm. Use drawings for the
        same material and thickness.
      </p>
      <form onSubmit={calculate}>
        <fieldset disabled={!!busy}>
          <div className="laser-costing__settings">
            {(
              [
                ["Sheet length (mm)", length, setLength],
                ["Sheet width (mm)", width, setWidth],
                ["Sheet thickness (mm)", thickness, setThickness],
              ] as const
            ).map(([label, value, setter]) => (
              <label key={label}>
                {label}
                <input
                  className="costing-input"
                  aria-label={label}
                  type="number"
                  min="0.001"
                  step="any"
                  required
                  value={value}
                  onChange={(e) => {
                    invalidate();
                    setter(e.target.value === "" ? "" : Number(e.target.value));
                  }}
                />
              </label>
            ))}
          </div>
          <div className="laser-costing__settings">
            <label>
              Part spacing (mm)
              <input
                className="costing-input"
                aria-label="Part spacing (mm)"
                type="number"
                min="0"
                step="any"
                value={spacing}
                placeholder={
                  thickness === "" ? "Defaults to thickness" : String(thickness)
                }
                onChange={(e) => {
                  invalidate();
                  setSpacing(
                    e.target.value === "" ? "" : Number(e.target.value)
                  );
                }}
              />
            </label>
            <label>
              Sheet edge margin (mm)
              <input
                className="costing-input"
                aria-label="Sheet edge margin (mm)"
                type="number"
                min="0"
                step="any"
                required
                value={edgeMargin}
                onChange={(e) => {
                  invalidate();
                  setEdgeMargin(
                    e.target.value === "" ? "" : Number(e.target.value)
                  );
                }}
              />
            </label>
          </div>
          <p className="costing-hint">
            Spacing defaults to sheet thickness unless entered above. Parts
            rotate in 90° steps and can interlock or use cut-outs. Set spacing
            and edge margin to suit your cutting process.{" "}
            {totalPieces.toLocaleString()} pieces selected.
          </p>
          <div className="laser-costing__upload">
            <label>
              Drawings without units
              <select
                className="costing-input"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
              >
                <option value="mm">Interpret as mm</option>
                <option value="inch">Interpret as inches</option>
              </select>
            </label>
            <label className="sheet-nesting__import">
              {busy === "upload" ? "Importing DXFs…" : "Import DXFs"}
              <input
                type="file"
                accept=".dxf"
                multiple
                aria-label="Nesting DXF files"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  void upload(files);
                }}
              />
            </label>
            <span>
              Up to 30 files, 10 MB each; {MAX_NEST_PIECES.toLocaleString()}{" "}
              total pieces.
            </span>
          </div>
          {rows.map((row) => (
            <article className="laser-costing__drawing" key={row.id}>
              <div className="laser-costing__drawing-header">
                <strong>{row.filename}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  aria-label={`Remove nesting ${row.filename}`}
                  onClick={() => {
                    invalidate();
                    setRows((prev) => prev.filter((r) => r.id !== row.id));
                  }}
                >
                  Remove
                </button>
              </div>
              {row.status === "error" ? (
                <p role="alert" className="text-danger">
                  {row.error}
                </p>
              ) : (
                <>
                  <p>
                    Part bounds: {row.length?.toFixed(3)} ×{" "}
                    {row.width?.toFixed(3)} mm
                  </p>
                  {row.warnings?.map((w) => (
                    <p className="costing-hint" key={w}>
                      {w}
                    </p>
                  ))}
                  <label>
                    Quantity required
                    <input
                      className="costing-input sheet-nesting__quantity"
                      type="number"
                      required
                      min="1"
                      max={MAX_NEST_PIECES}
                      step="1"
                      aria-label={`${row.filename} Quantity required`}
                      value={row.quantity}
                      onChange={(e) => {
                        invalidate();
                        const value =
                          e.target.value === "" ? "" : Number(e.target.value);
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, quantity: value } : r
                          )
                        );
                      }}
                    />
                  </label>
                </>
              )}
            </article>
          ))}
          {totalPieces > MAX_NEST_PIECES && (
            <p role="alert">
              Reduce the total to {MAX_NEST_PIECES.toLocaleString()} pieces or
              fewer.
            </p>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              !rows.length ||
              totalPieces > MAX_NEST_PIECES ||
              rows.some((r) => r.status === "error")
            }
          >
            {busy === "nesting"
              ? "Optimising contours…"
              : "Calculate sheets required"}
          </button>
        </fieldset>
      </form>
      {message && (
        <p role="alert" className="laser-costing__message mt-3">
          {message}
        </p>
      )}
      {result && sheet && (
        <section
          className="laser-costing__results"
          aria-label="Sheet nesting results"
        >
          <h3>
            {result.sheets.length}{" "}
            {result.sheets.length === 1 ? "sheet" : "sheets"} required ·{" "}
            {result.quantity} pieces
          </h3>
          <p>
            {result.length} × {result.width} × {result.thickness} mm ·{" "}
            {result.spacing ?? result.thickness} mm between pieces ·{" "}
            {result.edge_margin ?? 0} mm edge margin
          </p>
          <p className="costing-hint">
            Contour nesting with 0°, 90°, 180° and 270° rotation. Irregular
            parts can interlock and fit inside usable cut-outs. Clearance is at
            least the selected spacing, plus{" "}
            {result.clearance_allowance ?? 0.025} mm for curve approximation.
            This is an approximate layout, not a cutting program or guaranteed
            minimum.
            {result.time_limited &&
              " Search time limit reached; showing the best complete layout found."}
          </p>
          <div className="sheet-nesting__summary">
            <div>
              <strong>{utilization.toFixed(1)}%</strong>
              <span>Overall material utilisation</span>
            </div>
            <div>
              <strong>{(100 - utilization).toFixed(1)}%</strong>
              <span>Unused material, including reusable offcuts</span>
            </div>
            <div>
              <strong>{sheet.utilization.toFixed(1)}%</strong>
              <span>Selected sheet utilisation</span>
            </div>
          </div>
          <div className="laser-costing__drawing-header">
            <label>
              Sheet preview
              <select
                className="costing-input"
                aria-label="Sheet preview"
                value={sheetIndex}
                onChange={(e) => setSheetIndex(Number(e.target.value))}
              >
                {result.sheets.map((s, i) => (
                  <option value={i} key={i}>
                    Sheet {i + 1} · {s.placements.length} pieces ·{" "}
                    {s.utilization.toFixed(1)}% material utilisation
                  </option>
                ))}
              </select>
            </label>
            <span>{sheet.placements.length} pieces on this sheet</span>
          </div>
          <div className="sheet-nesting__toolbar" aria-label="Preview controls">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={zoom <= 1}
              onClick={() => setZoom(Math.max(1, zoom - 0.5))}
            >
              Zoom out
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={zoom >= 4}
              onClick={() => setZoom(Math.min(4, zoom + 0.5))}
            >
              Zoom in
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setZoom(1)}
            >
              Fit sheet
            </button>
          </div>
          <div className="sheet-nesting__viewport">
            <svg
              style={{
                width: `${zoom * 100}%`,
                maxHeight: zoom === 1 ? 700 : "none",
              }}
              className="sheet-nesting__plot"
              viewBox={`0 0 ${result.length} ${result.width}`}
              role="img"
              aria-label={`Nested layout for sheet ${sheetIndex + 1}`}
            >
              <defs>
                {result.parts.map((part) => (
                  <g key={part.id} id={`${previewId}-${part.id}`}>
                    <path
                      d={part.preview
                        ?.map(
                          (points) =>
                            `M${points
                              .map((point) => point.join(","))
                              .join(" L")} Z`
                        )
                        .join(" ")}
                      fill="currentColor"
                      fillOpacity="0.22"
                      fillRule="evenodd"
                      stroke="currentColor"
                      strokeWidth="1"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}
              </defs>
              <rect
                x="0"
                y="0"
                width={result.length}
                height={result.width}
                fill="#fff"
                stroke="#64748b"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              {(result.edge_margin ?? 0) > 0 && (
                <rect
                  x={result.edge_margin}
                  y={result.edge_margin}
                  width={result.length - 2 * (result.edge_margin ?? 0)}
                  height={result.width - 2 * (result.edge_margin ?? 0)}
                  fill="none"
                  stroke="#dc2626"
                  strokeDasharray="6 4"
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {sheet.placements.map((p, i) => {
                const part = result.parts.find((part) => part.id === p.partId);
                if (!part) return null;
                const color =
                  colors[result.parts.indexOf(part) % colors.length];
                return (
                  <g key={i}>
                    <title>
                      {part.filename} · {p.w.toFixed(2)} × {p.h.toFixed(2)} mm
                      {p.rotation ? ` · rotated ${p.rotation}°` : ""}
                    </title>
                    <g
                      transform={`translate(${p.x} ${
                        result.width - p.y
                      }) scale(1 -1)`}
                    >
                      <g
                        transform={`translate(${p.tx ?? 0} ${
                          p.ty ?? 0
                        }) rotate(${p.rotation ?? 0})`}
                      >
                        <use href={`#${previewId}-${part.id}`} color={color} />
                      </g>
                    </g>
                  </g>
                );
              })}
            </svg>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Piece</th>
                  <th>Required</th>
                  <th>On this sheet</th>
                </tr>
              </thead>
              <tbody>
                {result.parts.map((p, i) => (
                  <tr key={p.id}>
                    <td>
                      <span
                        className="sheet-nesting__swatch"
                        style={{ background: colors[i % colors.length] }}
                      />
                      {p.filename}
                    </td>
                    <td>{p.quantity}</td>
                    <td>
                      {sheet.placements.filter((s) => s.partId === p.id).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  );
}
